import { useEffect, useState } from "react";
import { Coins, ExternalLink } from "lucide-react";
import { useParams } from "react-router-dom";
import axios from "axios";

const money = new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN", minimumFractionDigits: 2 });
const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

const STATUS_META = {
  active: { label: "Activo", tone: "active" },
  pending: { label: "Pendiente", tone: "pending" },
  paused: { label: "En pausa", tone: "on_hold" },
  completed: { label: "Completado", tone: "completed" },
  archived: { label: "Archivado", tone: "archived" },
};

export default function PublicProjectPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    axios.get(`${API_URL}/public/projects/${token}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.detail || "Enlace no válido o revocado."));
  }, [token]);

  if (error) {
    return (
      <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", background: "#f8fafc", padding: 20 }}>
        <div style={{ background: "#fff", borderRadius: 20, padding: 40, maxWidth: 460, textAlign: "center", boxShadow: "0 8px 30px rgba(15,23,42,0.06)" }}>
          <h1 style={{ font: "900 24px Nunito, sans-serif", color: "#ef4444", marginTop: 0 }}>Enlace no válido</h1>
          <p style={{ color: "#64748b" }}>{error}</p>
        </div>
      </div>
    );
  }
  if (!data) return <div style={{ padding: 80, textAlign: "center", color: "#94a3b8" }}>Cargando…</div>;

  const p = data.project;
  const k = data.kpis || {};
  const meta = STATUS_META[p.status] || STATUS_META.active;
  const accent = p.color || "#4169e1";
  const remCls = k.remaining_status === "paid" ? "#10b981" : k.remaining_status === "pending" ? "#ef4444" : "#f59e0b";

  return (
    <div style={{ minHeight: "100vh", background: "#f8fafc", padding: "40px 20px" }} data-testid="public-project-view">
      <main style={{ maxWidth: 960, margin: "0 auto" }}>
        <div style={{ position: "relative", background: "linear-gradient(135deg, #fff, #f8fafc)", borderRadius: 24, border: "1px solid #e2e8f0", padding: "28px 32px", overflow: "hidden", marginBottom: 24 }}>
          <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 6, background: accent }} />
          <small style={{ font: "800 11px Nunito, sans-serif", letter: "0.05em", color: "#94a3b8", textTransform: "uppercase" }}>REPORTE DEL PROYECTO</small>
          <h1 style={{ font: "900 clamp(24px, 3vw, 34px) Nunito, sans-serif", color: "#0f172a", margin: "8px 0 4px" }}>{p.name}</h1>
          <p style={{ color: "#64748b", margin: 0, font: "600 15px Nunito, sans-serif" }}>{p.client_name || "Sin cliente"}{p.category ? ` · ${p.category}` : ""}</p>
          {p.description && <p style={{ color: "#94a3b8", marginTop: 10 }}>{p.description}</p>}
          <div style={{ display: "flex", gap: 24, marginTop: 20, paddingTop: 20, borderTop: "1px dashed #e2e8f0", flexWrap: "wrap" }}>
            <div><small style={{ font: "700 10px Nunito, sans-serif", color: "#94a3b8", textTransform: "uppercase" }}>Estatus</small><br /><b>{meta.label}</b></div>
            <div><small style={{ font: "700 10px Nunito, sans-serif", color: "#94a3b8", textTransform: "uppercase" }}>Contrato</small><br /><b>{money.format(k.contract_value || 0)}</b></div>
            <div><small style={{ font: "700 10px Nunito, sans-serif", color: "#94a3b8", textTransform: "uppercase" }}>Cobrado</small><br /><b style={{ color: "#10b981" }}>{money.format(k.collected || 0)}</b></div>
            <div><small style={{ font: "700 10px Nunito, sans-serif", color: "#94a3b8", textTransform: "uppercase" }}>Por cobrar</small><br /><b style={{ color: remCls }}>{money.format(k.remaining || 0)}</b></div>
            <div><small style={{ font: "700 10px Nunito, sans-serif", color: "#94a3b8", textTransform: "uppercase" }}>Avance</small><br /><b>{k.progress_pct || 0}%</b></div>
          </div>
        </div>

        {k.contract_value > 0 && (
          <div style={{ background: "#fff", borderRadius: 18, padding: 22, border: "1px solid #e2e8f0", marginBottom: 22 }}>
            <div style={{ display: "flex", justifyContent: "space-between", font: "700 12px Nunito, sans-serif", color: "#64748b", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.04em" }}>
              <span>Avance de cobro</span><span>{k.progress_pct}%</span>
            </div>
            <div style={{ height: 14, background: "#f1f5f9", borderRadius: 999, overflow: "hidden" }}>
              <div style={{ height: "100%", width: `${k.progress_pct || 0}%`, background: `linear-gradient(90deg, ${accent}, ${accent}aa)`, borderRadius: 999, transition: "width 0.8s" }} />
            </div>
          </div>
        )}

        <div style={{ background: "#fff", borderRadius: 18, border: "1px solid #e2e8f0", overflow: "hidden" }} data-testid="public-ledger">
          <div style={{ padding: "18px 22px", borderBottom: "1px solid #e2e8f0", display: "flex", alignItems: "center", gap: 10 }}>
            <Coins size={18} color="#4169e1" />
            <h2 style={{ margin: 0, font: "900 16px Nunito, sans-serif" }}>Movimientos</h2>
          </div>
          {data.ledger.length === 0 ? (
            <p style={{ padding: 30, textAlign: "center", color: "#94a3b8" }}>Sin movimientos aún.</p>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
              <thead style={{ background: "#f8fafc" }}>
                <tr>
                  <th style={{ padding: "12px 16px", textAlign: "left" }}>Fecha</th>
                  <th style={{ padding: "12px 16px", textAlign: "left" }}>Tipo</th>
                  <th style={{ padding: "12px 16px", textAlign: "left" }}>Concepto</th>
                  <th style={{ padding: "12px 16px", textAlign: "left" }}>Estado</th>
                  <th style={{ padding: "12px 16px", textAlign: "right" }}>Monto</th>
                </tr>
              </thead>
              <tbody>
                {data.ledger.map((r, i) => (
                  <tr key={i} style={{ borderTop: "1px solid #f1f5f9" }}>
                    <td style={{ padding: "10px 16px" }}>{r.date}</td>
                    <td style={{ padding: "10px 16px" }}>
                      <span style={{ padding: "3px 8px", borderRadius: 999, fontSize: 11, fontWeight: 800, background: r.type === "income" ? "#dcfce7" : "#fee2e2", color: r.type === "income" ? "#166534" : "#991b1b" }}>{r.type === "income" ? "Ingreso" : "Egreso"}</span>
                    </td>
                    <td style={{ padding: "10px 16px" }}><b>{r.concept}</b>{r.party && <div style={{ color: "#94a3b8", fontSize: 12 }}>{r.party}</div>}</td>
                    <td style={{ padding: "10px 16px", color: "#64748b", fontSize: 13 }}>{r.status}</td>
                    <td style={{ padding: "10px 16px", textAlign: "right", fontWeight: 800, color: r.amount >= 0 ? "#10b981" : "#ef4444" }}>{r.amount >= 0 ? "+" : ""}{money.format(r.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <p style={{ textAlign: "center", marginTop: 30, color: "#94a3b8", font: "600 12px Nunito, sans-serif" }}>
          Reporte generado con <a href="/" style={{ color: "#4169e1", textDecoration: "none", fontWeight: 800 }}>Mi Contador <ExternalLink size={11} /></a>
        </p>
      </main>
    </div>
  );
}
